import React, {useContext, useEffect, useState} from "react";
import Header from "../components/header/header";
import SidebarProvider from "../components/sidebar/sidebar";
import styles from '../../styles/main.module.css';
import {
    PrimaryButton,
    SelectInput,
    TextInput
} from "../../components/input/inputs";
import TableComponent from "../../components/table/table";
import Loading from "../../components/loading/loading";
import axiosInstance from "../../AxiosInstance";
import Title from "../../components/title/title";
import MyContext from "../../MyContext";
import {DateInput as DateInputAjonjolib} from "../../components/ajonjolib/inputs/ajonjolinput";
import {pendingInvoicesToString, statusToString, StatusChip} from "../../utils";
import Pagination from "../../components/ajonjolib/utils/pagination";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faChevronUp, faClockRotateLeft, faCog} from "@fortawesome/free-solid-svg-icons";
import {parseAmount} from "../../utils/utils";
import { purchaseOrderListHandlersRef } from "../../modals/bus/purchaseOrderListHandlersRef";
import { openOcDetail, openPurchaseOrderSettings } from "../../modals/bus/modalApi";
import AuditHistoryModal from "../../modals/audit_history_modal";
import filterStyles from "../../styles/consultationFilters.module.css";
import tableChrome from "../../components/table/consultationTableChrome.module.css";
import { purchaseOrderInvoiceAmountLeft } from "../../utils/currency";

export default function BuyOrdersProviders() {
    const [poConfig, setPoConfig] = useState([]);
    const [cols, setCols] = useState([]);
    const [rows, setRows] = useState([]);
    const [filteredRows, setFilteredRows] = useState([]);
    const [form, setForm] = useState({});
    const {isClient} = useContext(MyContext);
    const [userType, setUserType] = useState("");
    const [secondaryQuery, setSecondaryQuery] = useState("");
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [filtersCollapsed, setFiltersCollapsed] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageCount, setPageCount] = useState(1);
    const [maxItemsPerPage] = useState(25);
    const [auditHistory, setAuditHistory] = useState({
        show: false,
        mode: "purchase_order",
        entityId: null,
        titleLabel: "",
    });

    useEffect(() => {
        if(isClient) {
            setUserType("proveedor");
        } else {
            setUserType("cliente");
        }
    }, [isClient]);

    useEffect(() => {
        purchaseOrderListHandlersRef.current = {
            setRows,
            setFilteredRows,
            userType,
            replacePurchaseOrder: (nextOc) => {
                const withAmountLeft = {
                    ...nextOc,
                    amount_left: purchaseOrderInvoiceAmountLeft(nextOc),
                };
                setRows((prev) => prev.map((row) => (row.id === withAmountLeft.id ? withAmountLeft : row)));
                setFilteredRows((prev) => prev.map((row) => (row.id === withAmountLeft.id ? withAmountLeft : row)));
            },
        };
        return () => {
            purchaseOrderListHandlersRef.current = null;
        };
    }, [userType]);

    useEffect(() => {
        if(userType === "proveedor" || userType === "cliente") {
            getPurchaseOrderConfig();
        }
    }, [userType]);

    const getPurchaseOrderConfig = async () => {
        await axiosInstance.get('company/get_purchase_order_config/').then((response) => {
            const data = response.data;
            const ocStatusTooltip = (s) => {
                switch (s) {
                    case -1:
                        return 'Cargando...';
                    case 0:
                        return 'Pendiente';
                    case 1:
                        return 'Observado';
                    case 2:
                        return 'Completado';
                    case 3:
                        return 'En revisión';
                    case 4:
                        return 'Parcial';
                    default:
                        return 'Pendiente';
                }
            };
            const sortedData = data?.sort((a, b) => a.index - b.index);
            setPoConfig(sortedData);
            let retrievedColumns = []
            sortedData.forEach((item) => {
                if (!item.show) return;
                if (item.field === 'numeroOrden') {
                    retrievedColumns.push({ title: 'N° de Orden de Compra', internal: 'number' });
                } else if (item.field === 'tipoOrden') {
                    retrievedColumns.push({
                        title: 'Tipo OC',
                        internal: 'type',
                        tooltipText: (row) => (row.type === 0 ? 'Suministro' : 'Servicio'),
                        code: (row, data) => (
                            <StatusChip variant={data === 0 ? 'muted' : 'info'}>
                                {data === 0 ? 'Suministro' : 'Servicio'}
                            </StatusChip>
                        ),
                    });
                } else if (item.field === 'total') {
                    retrievedColumns.push({
                        title: 'Monto Total',
                        internal: 'total',
                        tooltipText: (row) => parseAmount(row.total),
                        code: (row, data) => (
                            <div className={'text-end'}>{parseAmount(data)}</div>
                        ),
                    });
                } else if (item.field === 'moneda') {
                    retrievedColumns.push({ title: 'Moneda', internal: 'currency.name' });
                } else if (item.field === 'fechaEmision') {
                    retrievedColumns.push({ title: 'Fecha de emisión', internal: 'date' });
                } else {
                    retrievedColumns.push({
                        title: item.name,
                        internal: `extra_data.${item.field}`,
                    });
                }
            });
            retrievedColumns.push({title: userType, internal: "id",
                tooltipText: (row) =>
                    userType === "proveedor"
                        ? row?.provider?.name ?? ''
                        : userType === "cliente"
                          ? row?.client?.name ?? ''
                          : '',
                code: (row, data) => {
                    if(userType === "proveedor") {
                        return row.provider.name;
                    } else if(userType === "cliente") {
                        return row.client.name;
                    }
                }});
            retrievedColumns.push({title: "RUC", internal: "id",
                tooltipText: (row) =>
                    userType === "proveedor"
                        ? row?.provider?.ruc ?? ''
                        : userType === "cliente"
                          ? row?.client?.ruc ?? ''
                          : '',
                code: (row, data) => {
                    if(userType === "proveedor") {
                        return row.provider.ruc;
                    } else if(userType === "cliente") {
                        return row.client.ruc;
                    }
                }});

            retrievedColumns.push({title: "Estado OC", internal: "status",
                tooltipText: (row) => ocStatusTooltip(row.status),
                code: (row, data) => statusToString(row, data) });
            retrievedColumns.push({title: "Atendido", internal: "has_pending_invoices",
                tooltipText: (row) => {
                    if (row.has_pending_invoices === true) return 'Por atender';
                    if (row.has_pending_invoices === false) return 'Atendido';
                    return '—';
                },
                code: (row, data) => pendingInvoicesToString(row, data) });
            retrievedColumns.push({
                title: "Acciones",
                internal: "type",
                code: (row, data) => (
                    <div className="d-inline-flex align-items-center gap-2 flex-nowrap">
                        <div
                            onClick={() => {
                                openOcDetail({
                                    oc: {
                                        ...row,
                                        amount_left: purchaseOrderInvoiceAmountLeft(row),
                                    },
                                    userType,
                                });
                            }}
                            role="button"
                            className={tableChrome.detailBtn}
                        >
                            Detalles
                        </div>
                        <button
                            type="button"
                            className={tableChrome.historyIconBtn}
                            title="Historial de eventos"
                            aria-label="Historial de eventos"
                            onClick={(e) => {
                                e.stopPropagation();
                                setAuditHistory({
                                    show: true,
                                    mode: "purchase_order",
                                    entityId: row.id,
                                    titleLabel: row.number ?? String(row.id),
                                });
                            }}
                        >
                            <FontAwesomeIcon icon={faClockRotateLeft} />
                        </button>
                    </div>
                ),
            });
            setCols(retrievedColumns);
        })
        query(1);
    }

    const query = (page = 1) => {
        setLoaded(false);
        const queryParams = Object.entries(form)
            .filter(([, value]) => value !== undefined && value !== null && String(value).trim() !== '')
            .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
            .join("&");
        const url = `purchase_order/?page=${page}&${queryParams}`;
        axiosInstance.get(url).then((response) => {
            const data = response.data;
            setRows(data.results);
            setFilteredRows(data.results);
            const totalPages = Math.ceil(data.count / maxItemsPerPage);
            setPageCount(totalPages);
            setLoaded(true);
        })
    }

    const downloadExcel = () => {
        const queryParams = Object.entries(form)
            .filter(([, value]) => value !== undefined && value !== null && String(value).trim() !== '')
            .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
            .join('&');
        const excelUrl = queryParams
            ? `purchase_order/excel/?${queryParams}`
            : 'purchase_order/excel/';
        axiosInstance.get(excelUrl, {
            responseType: 'blob'
        }).then((response) => {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'documentos-proveedor.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        });
    }

    useEffect(() => {
        if(secondaryQuery === undefined || secondaryQuery === "") setFilteredRows(rows);
        const lowercaseQuery = secondaryQuery.toLowerCase();
        const filtered = rows.filter((row) => {
            return (row?.client?.name?.toLowerCase().includes(lowercaseQuery)
                || row?.client?.ruc?.toString().includes(lowercaseQuery)
                || row?.number?.toString().includes(lowercaseQuery)
                || row?.total?.toString().includes(lowercaseQuery)
                || (row?.type === 0 ? "suministro".includes(lowercaseQuery) : "servicio".includes(lowercaseQuery))
            );
        })
        setFilteredRows(filtered);
    }, [secondaryQuery, rows]);

    return (
        <div className={styles.mainContainer}>

            <Header setSidebarOpen={setSidebarOpen}/>
            <SidebarProvider isOpen={sidebarOpen} index={0}/>

            <div className={styles.container} style={!sidebarOpen ? {marginLeft: '80px'} : {marginLeft: '285px'}}>
                <Title name={'Orden de Compra'} consultation>
                    {userType !== 'cliente' && <div className={'d-flex align-items-center'} style={{userSelect: 'none', webkitUserSelect: 'none', cursor: 'pointer'}}
                         onClick={() => {
                             openPurchaseOrderSettings({ data: poConfig });
                         }}>
                        <div className={'pe-2'}>Configurar</div>
                        <FontAwesomeIcon icon={faCog}/>
                    </div>
                    }
                </Title>

                <div className={[filterStyles.filterShell, filtersCollapsed ? filterStyles.filterShellCollapsed : ''].filter(Boolean).join(' ')}>
                    <div className={filterStyles.filterSectionHead}>
                        <div className={filterStyles.sectionTitle}>Filtros</div>
                        <button
                            type="button"
                            className={filterStyles.filterCollapseToggle}
                            aria-expanded={!filtersCollapsed}
                            aria-label={filtersCollapsed ? 'Mostrar filtros' : 'Ocultar filtros'}
                            onClick={() => setFiltersCollapsed((c) => !c)}
                        >
                            <FontAwesomeIcon
                                icon={faChevronUp}
                                className={[filterStyles.filterChevron, filtersCollapsed ? filterStyles.filterChevronCollapsed : ''].filter(Boolean).join(' ')}
                            />
                        </button>
                    </div>

                    <div className={filterStyles.filterCollapseOuter}>
                        <div className={filterStyles.filterCollapseInner}>
                            {userType !== 'cliente' ?
                                <div className={filterStyles.grid}>
                                    <div className={filterStyles.stack}>
                                        <SelectInput greyBackground={true} name={'Tipo OC'} placeholder={'Tipo OC'} hideName={true} form={form} setForm={setForm} internalName={'oc_type'} options={[{id: 0, name: 'Suministro'}, {id: 1, name: 'Servicio'}]}/>
                                        <DateInputAjonjolib placeholder={'Fecha de emisión'} onChange={(data) => {
                                            setForm({
                                                ...form,
                                                'issue_date': data.startDate.toLocaleString('es')?.split(',')[0].replace(/\//g, '-'),
                                                'issue_date_end': data.endDate.toLocaleString('es')?.split(',')[0].replace(/\//g, '-')
                                            })
                                        }}/>
                                    </div>

                                    <div className={filterStyles.stack}>
                                        <TextInput name={'NOC'} placeholder={'N° Orden de Compra'} internalName={'number'} form={form} setForm={setForm} hideName={true}/>
                                        <SelectInput greyBackground={true} name={'Estado OC'} placeholder={'Estado OC'} hideName={true} form={form} setForm={setForm} internalName={'status'} options={[{id: '', name: 'Todos'}, {id: 0, name: 'Pendiente'}, {id: 1, name: 'Observado'}, {id: 2, name: 'Completado'}, {id: 3, name: 'En revisión'}, {id: 4, name: 'Parcial'}]}/>
                                    </div>

                                    <div className={filterStyles.stack}>
                                        <TextInput name={'RUC'} placeholder={'R.U.C. del Proveedor'} internalName={'ruc'} form={form} setForm={setForm} hideName={true}/>
                                        <SelectInput greyBackground={true} name={'currency'} placeholder={'Moneda'} internalName={'currency'} options={[{id: 1, name: 'Soles'}, {id: 2, name: 'Dólares'}, {id: 3, name: "Euros"}]} form={form} setForm={setForm} hideName={true}/>
                                    </div>

                                    <div className={filterStyles.stack}>
                                        <TextInput name={'NAME'} placeholder={'Nombre del Proveedor'} internalName={'name'} form={form} setForm={setForm} hideName={true}/>
                                        <TextInput name={'Monto Total'} placeholder={'Monto Total'} internalName={'total'} form={form} setForm={setForm} hideName={true} />
                                    </div>
                                </div>
                                :
                                <div className={filterStyles.grid}>
                                    <div className={filterStyles.stack}>
                                        <SelectInput greyBackground={true} name={'Tipo OC'} placeholder={'Tipo OC'} hideName={true} form={form} setForm={setForm} internalName={'oc_type'} options={[{id: 0, name: 'Suministro'}, {id: 1, name: 'Servicio'}]}/>
                                        <DateInputAjonjolib placeholder={'Fecha de emisión'} onChange={(data) => {
                                            setForm({
                                                ...form,
                                                'issue_date': data.startDate.toLocaleString('es')?.split(',')[0].replace(/\//g, '-'),
                                                'issue_date_end': data.endDate.toLocaleString('es')?.split(',')[0].replace(/\//g, '-')
                                            })
                                        }}/>
                                    </div>

                                    <div className={filterStyles.stack}>
                                        <TextInput name={'NOC'} placeholder={'N° Orden de Compra'} internalName={'number'} form={form} setForm={setForm} hideName={true}/>
                                        <SelectInput greyBackground={true} name={'Estado OC'} placeholder={'Estado OC'} hideName={true} form={form} setForm={setForm} internalName={'status'} options={[{id: '', name: 'Todos'}, {id: 0, name: 'Pendiente'}, {id: 1, name: 'Observado'}, {id: 2, name: 'Completado'}, {id: 3, name: 'En revisión'}, {id: 4, name: 'Parcial'}]}/>
                                    </div>

                                    <div className={filterStyles.stack}>
                                        <TextInput name={'RUC'} placeholder={'R.U.C. del Cliente'} internalName={'ruc'} form={form} setForm={setForm} hideName={true}/>
                                        <SelectInput greyBackground={true} name={'currency'} placeholder={'Moneda'} internalName={'currency'} options={[{id: 1, name: 'Soles'}, {id: 2, name: 'Dólares'}, {id: 3, name: "Euros"}]} form={form} setForm={setForm} hideName={true}/>
                                    </div>

                                    <div className={filterStyles.stack}>
                                        <TextInput name={'NAME'} placeholder={'Nombre del Cliente'} internalName={'name'} form={form} setForm={setForm} hideName={true}/>
                                        <TextInput name={'Monto Total'} placeholder={'Monto Total'} internalName={'total'} form={form} setForm={setForm} hideName={true} />
                                    </div>
                                </div>
                            }

                            <div className={filterStyles.filterSearchRow}>
                                <div className={filterStyles.filterSearchRowInput}>
                                    <TextInput
                                        name={'Datos ítems facturas / JSON'}
                                        placeholder={'Buscar texto en ítems de facturas de la OC (JSON)'}
                                        internalName={'extra_data_q'}
                                        form={form}
                                        setForm={setForm}
                                        hideName={true}
                                    />
                                </div>
                                <div className={filterStyles.filterSearchRowBtn}>
                                    <PrimaryButton variant="quiet" name={'CONSULTAR'} onClick={() => {
                                        setCurrentPage(1);
                                        query(1);
                                    }}/>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className={filterStyles.secondaryShell}>
                    <div className={'d-flex flex-row align-items-center justify-content-between flex-wrap gap-1'}>
                        <div className={'d-flex flex-row align-items-center flex-wrap gap-1'}>
                            <div>Opciones de listado:</div>
                            <div className={filterStyles.excelBtn} onClick={() => downloadExcel()}>Excel</div>
                        </div>
                        <div className={'d-flex align-items-center flex-wrap'}>
                            <div className={'pe-2'}>Buscar:</div>
                            <input className={filterStyles.inlineSearchInput} value={secondaryQuery} onChange={(event) => {
                                setSecondaryQuery(event.target.value);
                            }}/>
                        </div>
                    </div>
                </div>

                <div className={'pb-5'}>
                    {
                        rows.length === 0 ? (loaded ?
                                    <div>No se encontraron resultados</div>:
                                    <Loading/>
                            ) :
                            <React.Fragment>
                                <TableComponent
                                    withIndex={true}
                                    currentPage={currentPage}
                                    pageSize={25}
                                    cols={cols}
                                    rows={filteredRows}
                                    pull={setRows}
                                    fetchUrl={'purchase_order'}
                                    defaultFilters={{}}
                                    rowHover
                                    consultationChrome
                                />
                                <Pagination currentPage={currentPage} count={pageCount} arrows={true} className={'mt-3'} onInput={(page) => {
                                    setCurrentPage(page);
                                    query(page);
                                }}/>
                            </React.Fragment>
                    }
                </div>
            </div>

            <AuditHistoryModal
                show={auditHistory.show}
                onHide={() => setAuditHistory((s) => ({ ...s, show: false }))}
                mode={auditHistory.mode}
                entityId={auditHistory.entityId}
                titleLabel={auditHistory.titleLabel}
            />
        </div>
    );
}
